/**
 * Scenario types for oil and gas field projects
 */

export interface ScenarioBase {
  name: string;
  description?: string;
  coordinate?: string | null;
  field_id?: string;
  field_type: string;
  unit_id: number;
  is_public: boolean;  // Deprecated, use visibility_status_id
  visibility_status_id?: string;
  is_draft: boolean;
  to_date: string; // ISO date string
  mode?: 'ml' | 'manual';
  ml_mode?: 'default' | 'custom' | 'analogue';
  forecast_engine?: 'type_well' | 'material_balance';
  custom_model_id?: string | null;
  n_simmulations?: number;
  deviation?: number;
}

export interface ScenarioCreate extends ScenarioBase {}

/** POST /scenarios/{id}/copy */
export interface ScenarioCopyRequest {
  name?: string;
}

export interface ScenarioUpdate {
  name?: string;
  description?: string;
  coordinate?: string | null;
  field_id?: string;
  field_type?: string;
  unit_id?: number;
  is_public?: boolean;  // Deprecated, use visibility_status_id
  visibility_status_id?: string;
  is_draft?: boolean;
  to_date?: string;
  n_simmulations?: number;
  deviation?: number;
  calculated_npv?: number;
  mode?: 'ml' | 'manual';
  ml_mode?: 'default' | 'custom' | 'analogue';
  forecast_engine?: 'type_well' | 'material_balance';
  custom_model_id?: string | null;
}

export interface ScenarioResponse extends ScenarioBase {
  id: string;
  calculated_npv?: number;
  created_at: string;
  updated_at: string;
  organization_id: string;
  author_id: string;
  layers_count?: number;
  wells_count?: number;
}

export interface ScenarioList {
  scenarios: ScenarioResponse[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface ScenarioCalculationRequest {
  scenario_id: string;
  parameters: Record<string, any>;
}

export interface ScenarioCalculationResponse {
  scenario_id: string;
  calculation_id: string;
  results: Record<string, any>;
  calculated_npv: number;
  calculation_date: string;
}

// Field type constants
export const FIELD_TYPES = {
  OIL: 'oil',
  GAS: 'gas',
  OIL_GAS: 'oil_gas',
} as const;

/** Production rate units (scenarios.unit_id). */
export const SCENARIO_UNITS = {
  TONS: 1,
  BARRELS: 2,
} as const;


// Helper functions
export const getFieldTypeLabel = (fieldType: string): string => {
  const labels: Record<string, string> = {
    'oil': 'Oil Field',
    'gas': 'Gas Field',
    'oil_gas': 'Oil & Gas Field',
  };
  return labels[fieldType] || fieldType;
};

export const getFieldTypeIcon = (fieldType: string): string => {
  const icons: Record<string, string> = {
    'oil': '🛢️',
    'gas': '🔥',
    'oil_gas': '⚡',
  };
  return icons[fieldType] || '📋';
};


export const formatCurrency = (value?: number): string => {
  if (value === undefined || value === null) return 'N/A';

  if (Math.abs(value) >= 1e9) {
    return `$${(value / 1e9).toFixed(2)}B`;
  } else if (Math.abs(value) >= 1e6) {
    return `$${(value / 1e6).toFixed(2)}M`;
  } else if (Math.abs(value) >= 1e3) {
    return `$${(value / 1e3).toFixed(2)}K`;
  } else {
    return `$${value.toFixed(2)}`;
  }
};

export const formatPercentage = (value?: number): string => {
  if (value === undefined || value === null) return 'N/A';
  return `${(value * 100).toFixed(2)}%`;
};

export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatDateTime = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const calculateForecastYears = (startDate: string, endDate: string): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.ceil(diffDays / 365);
};
